/**
 * The Change SKU dialog's placement decisions (card #545, Jake 2026-10-28).
 *
 * When the NEW style does not carry a saved placement's location, the dialog
 * asks what to do before the swap applies: move it to a location the new
 * style carries, or remove it. The answers travel with the SKU in ONE server
 * call (POST /campaign_products/<id>/swap_sku/), so a broken placement is
 * never stored through the swap.
 *
 * "Carries" is the same rule the editor's flag uses (isZoneValidForSku over
 * the style's live Artifi views). With the new style's views unknown, no
 * placement is judged stranded (fail open, as everywhere): the swap takes the
 * autosave path and the server's hide rule is the safety net.
 */
import { isZoneValidForSku, liveViewCodeForSlug } from "@/utils/placementZoneToViewCode";
import { skuViewsAuthority, zoneOptionsForSku } from "@/utils/placementLocationOptions";

/** The option value that means "remove this placement". */
export const REMOVE_DECISION = "__remove__";

/** The saved placements the new style does not carry ([] with no views). */
export function strandedPlacements(placements, views) {
    if (!skuViewsAuthority(views)) return [];
    return (Array.isArray(placements) ? placements : []).filter(
        (p) =>
            p
            && p.id != null
            && p.placement_zone_slug
            && !isZoneValidForSku(p.placement_zone_slug, views),
    );
}

/**
 * The location a kept placement will hold after the swap, as a NEW-style view
 * code, or null (removed, or still stranded).
 */
function finalCode(p, views, decisions) {
    const decision = decisions?.[p.id];
    if (decision?.remove) return null;
    if (decision?.view_code) return decision.view_code;
    return liveViewCodeForSlug(p.placement_zone_slug, views);
}

/**
 * One stranded placement's choices: the new style's locations no OTHER kept
 * placement will hold (one decoration per location), then Remove.
 */
export function decisionOptions(placement, { placements, views, decisions }) {
    const taken = new Set(
        (Array.isArray(placements) ? placements : [])
            .filter((p) => p && p.id !== placement.id)
            .map((p) => finalCode(p, views, decisions))
            .filter((c) => c != null),
    );
    const locations = zoneOptionsForSku(views, [])
        .map((o) => ({ value: String(o.value), label: o.label }))
        .filter((o) => !taken.has(o.value));
    return [...locations, { value: REMOVE_DECISION, label: "Remove this placement" }];
}

/** Every stranded placement has a decision. */
export function decisionsComplete(stranded, decisions) {
    return (Array.isArray(stranded) ? stranded : []).every((p) => {
        const d = decisions?.[p.id];
        return Boolean(d && (d.remove === true || d.view_code));
    });
}

/** The swap call's body: the SKU, its supplier and style, and the decisions. */
export function swapRequestBody({ selected, supplierOption, stranded, decisions }) {
    const body = {
        product_item_number: selected.id,
        supplier_style_id: selected.style_id ?? null,
    };
    if (supplierOption?.vendorId != null) {
        body.supplier_id = supplierOption.vendorId;
        body.supplier_name = supplierOption.title;
    }
    body.placements = (stranded || []).map((p) => {
        const d = decisions?.[p.id] || {};
        // store.decorations carries JSON:API ids (strings); the server
        // requires integers.
        const id = Number(p.id);
        return d.remove ? { id, remove: true } : { id, view_code: d.view_code };
    });
    return body;
}
